import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BadgeCheck,
  Check,
  FileText,
  FileWarning,
  Layers,
  Lock,
  RefreshCw,
  RotateCcw,
  Search,
  Square,
  SquareCheck,
  X,
} from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { useContexto } from '@/hooks/useContexto';
import { useCracha } from '@/hooks/useCracha';
import { useSessao } from '@/hooks/useSessao';
import { PedirCracha, SeloCracha } from '@/componentes/operacao/Cracha';
import { useOcioso } from '@/hooks/useOcioso';
import { cn, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { Segmentado } from '@/componentes/ui/segmentado';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import {
  CabecalhoPagina,
  Cartao,
  CorpoCartao,
  Esqueleto,
  Kpi,
  Pilula,
} from '@/componentes/ui/primitivos';

/**
 * Faturamento — a última etapa: separado, conferido, agora vira nota.
 *
 * É a tela do MegaFaturamento (o app na AWS) refeita: o pedido já conferido
 * aparece na grade, o operador marca o que vai faturar, confere o volume e
 * manda. Nada de bipar — bipar é o caminho do coletor, outro aparelho, outro
 * momento.
 *
 * A regra fiscal é a mesma procedure que o app antigo chama,
 * `GERAR_NF_FATURAMENTO_TELEVENDAS`. O que muda é o que se vê em volta dela:
 *
 *  - **o crachá não é digitado.** No MegaFaturamento o login É o número do
 *    crachá, sem senha: quem souber o número de outro assume a fila do outro.
 *    Aqui ele vem da sessão (`CRACHAS.USUARIO`), e digitar só entra como
 *    remendo para quem ainda não tem vínculo no cadastro.
 *  - **a recusa tem motivo.** A procedure devolve o texto por `@RESULT
 *    OUTPUT`; o app antigo passa `''` ali e joga fora, então o operador vê
 *    "erro" e precisa abrir OUTRA tela para descobrir o quê. Aqui a frase do
 *    ERP aparece na linha do pedido.
 *  - **o lote não trava a tela.** Lá é um POST por pedido atrás de um modal
 *    bloqueante que, no fim, mostra dois números agregados; se a conexão cai
 *    no meio, ninguém sabe onde parou. Aqui também é um por vez (a procedure
 *    é pesada e emite documento numerado), mas cada linha muda de cor assim
 *    que volta, e dá para parar no meio.
 *  - **a onda existe.** O agrupamento por modal, que lá é só um
 *    `ORDER BY E.DESCRICAO` — o operador arrasta o mouse por um trecho
 *    contíguo torcendo para não pegar a linha errada —, aqui é bloco com
 *    seleção própria.
 *  - **nada de recarregar sozinho.** Lá a página se recarrega a cada 90 s de
 *    ociosidade e leva junto tudo que foi digitado nos campos de volume.
 *
 * O que NÃO muda, de propósito: a regra fiscal, as frases de recusa e o
 * registro em `FATURAMENTOS_ERROS` — a mesma tabela do app antigo, para a
 * operação não passar a ter duas listas de "o que falhou".
 */

interface PedidoFila {
  televenda: number;
  empresa: number;
  data: string | null;
  canal: string | null;
  modal: string | null;
  cidade: string | null;
  uf: string | null;
  itens: number;
  produtos: { produto: number; quantidade: number; descricao: string }[];
  cracha: string | null;
}

interface Falha {
  id: number;
  televenda: number;
  empresa: number | null;
  erro: string;
  quando: string;
  resolvido: boolean;
}

interface RespostaFila {
  empresa: number;
  cracha: string | null;
  crachaDaSessao: string | null;
  escopo: 'meu' | 'empresa';
  fila: PedidoFila[];
  falhas: Falha[];
  escritaLiberada: boolean;
}

/** Uma NF-e pendente na SEFAZ: recusada, com falha no envio ou aguardando o robô. */
interface Rejeicao {
  /** Nota de venda (NF_FATURAMENTO) ou de devolução (DEV_PRODUTOS_CAIXAS). */
  origem: 'venda' | 'devolucao';
  televenda: number | null;
  devolucao: number | null;
  nfNumero: string | null;
  empresa: number | null;
  empresaNome: string | null;
  dataNf: string | null;
  quando: string;
  etapa: string;
  motivo: string;
}

interface RespostaRejeicoes {
  empresa: number | 'todas';
  rejeicoes: Rejeicao[];
  pendentes: number;
}

interface Resultado {
  televenda: number;
  ok: boolean;
  mensagem: string;
  volume: number;
}

/** Uma linha do que aconteceu nesta sessão de trabalho. */
interface Registro {
  chave: string;
  televenda: number;
  tom: 'ok' | 'recusado' | 'falhou';
  mensagem: string;
  volume: number | null;
  quando: number;
}

const SEM_MODAL = 'Sem modal definido';

async function faturar(
  televenda: number,
  volume: number,
  cracha: string,
  informado: boolean,
): Promise<Resultado> {
  return api.post<Resultado>('/api/faturamento/faturar', {
    televenda,
    volume,
    ...(informado ? { cracha } : {}),
  });
}

export function Faturamento(): JSX.Element {
  const { empresa } = useContexto();
  // Admin vê as rejeições de todas as empresas de uma vez, com a empresa em cada linha.
  const todasEmpresas = useSessao().temPermissao('admin.total');
  const cracha = useCracha();

  const [aba, definirAba] = React.useState<'fila' | 'problemas' | 'rejeitadas'>('fila');

  /*
   * Recarregar sozinho, mas só quando não atrapalha.
   *
   * A fila muda porque OUTRAS pessoas estão conferindo e faturando, então uma
   * tela aberta o dia todo fica desatualizada. Mas o MegaFaturamento mostra
   * como não fazer: ele se recarrega a cada 90 s de ociosidade e leva junto o
   * que estava digitado nos campos de volume.
   *
   * Aqui são DUAS condições, e as duas precisam valer:
   *
   *  - `ocioso`: ninguém tocou em teclado, mouse ou tela há 30 s (e a aba está
   *    visível — aba em segundo plano não é ociosidade, é ausência);
   *  - `!trabalhoEmCurso`: não há pedido marcado, volume alterado nem lote
   *    rodando. Marcar oito pedidos, sair para buscar um café e voltar sem a
   *    seleção seria o mesmo defeito com outro nome.
   */
  const ocioso = useOcioso(30_000);
  const [trabalhoEmCurso, definirTrabalhoEmCurso] = React.useState(false);
  const [escopo, definirEscopo] = React.useState<'meu' | 'empresa'>('meu');
  const [registros, definirRegistros] = React.useState<Registro[]>([]);

  const consulta = useQuery<RespostaFila>({
    queryKey: ['faturamento', 'fila', empresa, escopo, cracha.cracha],
    queryFn: () =>
      api.get<RespostaFila>('/api/faturamento/fila', {
        empresa,
        escopo,
        cracha: cracha.daSessao ? undefined : cracha.cracha,
      }),
    enabled: empresa !== null && cracha.cracha !== null,
    staleTime: 30_000,
    refetchInterval: ocioso && !trabalhoEmCurso && aba === 'fila' ? 30_000 : false,
    // Sem isto, voltar para a aba dispararia uma recarga no exato instante em
    // que a pessoa volta a mexer — o oposto do que se quer.
    refetchOnWindowFocus: false,
  });

  /*
   * As rejeições da SEFAZ, só quando a aba é aberta.
   *
   * A consulta varre o NFE_LOG e custa ~575 ms; a fila recarrega a cada 30 s.
   * Carregar junto faria quem está faturando pagar por uma aba que talvez nem
   * abra — por isso `enabled` depende da aba estar ativa.
   *
   * `staleTime` curto: quem abre esta aba acabou de corrigir uma nota e quer
   * ver se ela saiu. Com 2 min, voltar à aba mostrava a lista velha sem buscar.
   */
  const rejeicoes = useQuery<RespostaRejeicoes>({
    queryKey: ['faturamento', 'rejeicoes', todasEmpresas ? 'todas' : empresa],
    queryFn: () =>
      api.get<RespostaRejeicoes>('/api/faturamento/rejeicoes', {
        empresa: todasEmpresas ? 'todas' : empresa,
        dias: 15,
      }),
    enabled: empresa !== null && aba === 'rejeitadas',
    staleTime: 10_000,
  });

  // O Atualizar do topo recarrega a aba que está na tela. Antes ele só
  // recarregava a fila, e na aba da SEFAZ parecia não fazer nada.
  const atualizarAba = aba === 'rejeitadas' ? rejeicoes : consulta;

  const anotar = React.useCallback((registro: Omit<Registro, 'chave' | 'quando'>) => {
    definirRegistros((anteriores) =>
      [
        { ...registro, chave: `${registro.televenda}-${Date.now()}`, quando: Date.now() },
        ...anteriores,
      ].slice(0, 80),
    );
  }, []);

  const faturados = registros.filter((r) => r.tom === 'ok').length;
  const naFila = consulta.data?.fila.length ?? 0;
  const pendentes = consulta.data?.falhas.filter((f) => !f.resolvido).length ?? 0;

  return (
    <div className="space-y-4">
      <CabecalhoPagina
        sobrelinha="Operação"
        titulo="Faturamento"
        descricao="Pedido conferido vira nota. Marque na grade o que vai faturar, confira o volume e mande."
        icone={<FileText className="h-5 w-5" />}
        acoes={
          <>
            <SeloCracha cracha={cracha} />
            {/*
              Diz em voz baixa o que a tela está fazendo.

              Uma lista que muda sozinha sem avisar assusta — e faz o operador
              desconfiar do número. Duas frases curtas resolvem: quando ele
              está no meio de algo, a tela declara que NÃO vai mexer.
            */}
            <span className="hidden text-[11px] text-textoFraco sm:inline">
              {trabalhoEmCurso
                ? 'não atualiza enquanto você trabalha'
                : ocioso
                  ? 'atualizando sozinha'
                  : 'atualiza sozinha após 30s parado'}
            </span>
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              onClick={() => void atualizarAba.refetch()}
              carregando={atualizarAba.isFetching}
            >
              <RefreshCw className="h-4 w-4" />
              Atualizar
            </Botao>
          </>
        }
      />

      {consulta.data && !consulta.data.escritaLiberada ? (
        <p className="flex items-start gap-2 rounded-xl bg-status-atencaoFundo px-3 py-2 text-[12.5px] text-status-atencao">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            A trava de escrita do WMS está ligada (<code>WMS_ALLOW_WRITES=false</code>). A grade, as
            checagens e os motivos de recusa funcionam; <strong>nenhuma nota é emitida</strong>.
          </span>
        </p>
      ) : null}

      {cracha.cracha === null ? (
        <PedirCracha aoInformar={cracha.informar} />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Kpi
              rotulo="Esperando nota"
              valor={numero(naFila)}
              nota={escopo === 'meu' ? `fila do crachá ${cracha.cracha}` : 'empresa inteira'}
              tom="marca"
              icone={<Layers className="h-4 w-4" />}
            />
            <Kpi
              rotulo="Faturados nesta sessão"
              valor={numero(faturados)}
              nota="desde que a tela abriu"
              tom="ok"
              icone={<Check className="h-4 w-4" />}
            />
            <Kpi
              rotulo="Parados com erro"
              valor={numero(pendentes)}
              nota={pendentes > 0 ? 'ainda sem nota' : 'nada travado nos últimos 7 dias'}
              tom={pendentes > 0 ? 'erro' : 'neutro'}
              icone={<AlertTriangle className="h-4 w-4" />}
            />
          </div>

          <Segmentado
            valor={aba}
            aoMudar={definirAba}
            rotuloAcessivel="O que mostrar"
            opcoes={[
              {
                valor: 'fila',
                rotulo: `A faturar${naFila ? ` (${naFila})` : ''}`,
                icone: <Layers className="h-4 w-4" />,
              },
              {
                valor: 'problemas',
                rotulo: `Problemas${pendentes ? ` (${pendentes})` : ''}`,
                icone: <AlertTriangle className="h-4 w-4" />,
              },
              {
                valor: 'rejeitadas',
                rotulo: `Pendentes na SEFAZ${
                  rejeicoes.data?.pendentes ? ` (${rejeicoes.data.pendentes})` : ''
                }`,
                icone: <FileWarning className="h-4 w-4" />,
              },
            ]}
          />

          {aba === 'fila' ? (
            <PainelFila
              consulta={consulta}
              escopo={escopo}
              aoMudarEscopo={definirEscopo}
              cracha={cracha.cracha}
              informado={!cracha.daSessao}
              aoAnotar={anotar}
              aoMudarTrabalho={definirTrabalhoEmCurso}
            />
          ) : aba === 'problemas' ? (
            <PainelProblemas
              consulta={consulta}
              cracha={cracha.cracha}
              informado={!cracha.daSessao}
              aoAnotar={anotar}
            />
          ) : (
            <PainelRejeicoes consulta={rejeicoes} />
          )}

          {registros.length > 0 ? (
            <Historico registros={registros} aoLimpar={() => definirRegistros([])} />
          ) : null}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Crachá
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// A grade — o coração da tela
// ---------------------------------------------------------------------------

interface EstadoLote {
  rodando: boolean;
  feitos: number;
  total: number;
  porPedido: Record<number, { ok: boolean; mensagem: string }>;
}

function PainelFila({
  consulta,
  escopo,
  aoMudarEscopo,
  cracha,
  informado,
  aoAnotar,
  aoMudarTrabalho,
}: {
  consulta: ReturnType<typeof useQuery<RespostaFila>>;
  escopo: 'meu' | 'empresa';
  aoMudarEscopo: (e: 'meu' | 'empresa') => void;
  cracha: string;
  informado: boolean;
  aoAnotar: (r: Omit<Registro, 'chave' | 'quando'>) => void;
  /** Avisa o pai que há trabalho em curso — e que recarregar destruiria. */
  aoMudarTrabalho: (emCurso: boolean) => void;
}): JSX.Element {
  /*
   * UMA linha por televenda — cinto e suspensório.
   *
   * A consulta já agrupa (FATURAMENTO_STATUS não tem chave única por pedido),
   * mas pedido repetido nesta tela significa nota emitida duas vezes, e isso
   * não se desfaz. Então a garantia é repetida aqui, onde a lista vira linha.
   */
  const fila = React.useMemo(() => {
    const vistos = new Set<number>();
    return (consulta.data?.fila ?? []).filter((p) => {
      if (vistos.has(p.televenda)) return false;
      vistos.add(p.televenda);
      return true;
    });
  }, [consulta.data]);

  const [busca, definirBusca] = React.useState('');
  const [marcados, definirMarcados] = React.useState<Set<number>>(new Set());
  const [volumes, definirVolumes] = React.useState<Record<number, number>>({});
  const [lote, definirLote] = React.useState<EstadoLote>({
    rodando: false,
    feitos: 0,
    total: 0,
    porPedido: {},
  });
  const cancelar = React.useRef(false);

  /*
   * O que conta como "estou no meio de alguma coisa".
   *
   * Pedido marcado, volume alterado ou lote rodando. Qualquer um dos três faz
   * o auto-refresh esperar — recarregar a fila apagaria a seleção e os volumes
   * digitados, que é exatamente o defeito do app antigo.
   */
  const emCurso = lote.rodando || marcados.size > 0 || Object.keys(volumes).length > 0;

  React.useEffect(() => {
    aoMudarTrabalho(emCurso);
  }, [emCurso, aoMudarTrabalho]);

  /**
   * O volume de um pedido: 1, até que alguém diga outra coisa.
   *
   * O MegaFaturamento manda a quantidade de ITENS quando o campo fica em
   * branco — um pedido de 6 unidades vira 6 volumes sem ninguém pedir. Na
   * prática quase tudo sai em uma caixa só, então o padrão que erra menos é 1,
   * e quem embalou em mais de uma muda no próprio pedido.
   *
   * O número aparece escrito, nunca implícito, e a caixa fica destacada quando
   * deixa de ser 1 — é o campo que decide quantas etiquetas de transporte o
   * pedido ganha.
   */
  const volumeDe = (p: PedidoFila): number => volumes[p.televenda] ?? 1;

  const visiveis = React.useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (t === '') return fila;
    return fila.filter(
      (p) =>
        String(p.televenda).includes(t) ||
        (p.canal ?? '').toLowerCase().includes(t) ||
        (p.modal ?? '').toLowerCase().includes(t) ||
        (p.cidade ?? '').toLowerCase().includes(t) ||
        (p.uf ?? '').toLowerCase().includes(t),
    );
  }, [fila, busca]);

  /**
   * As ondas: um bloco por modal.
   *
   * No MegaFaturamento isso é só o `ORDER BY E.DESCRICAO` — a lista chega
   * agrupada e o operador arrasta o mouse por um trecho contíguo torcendo
   * para não pegar a linha errada. Bloco com seleção própria é a mesma
   * intenção, dita em voz alta.
   */
  const ondas = React.useMemo(() => {
    const mapa = new Map<string, PedidoFila[]>();
    for (const p of visiveis) {
      const chave = p.modal ?? SEM_MODAL;
      const lista = mapa.get(chave);
      if (lista) lista.push(p);
      else mapa.set(chave, [p]);
    }
    return [...mapa.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [visiveis]);

  const alternar = (televenda: number): void =>
    definirMarcados((antes) => {
      const proximo = new Set(antes);
      if (proximo.has(televenda)) proximo.delete(televenda);
      else proximo.add(televenda);
      return proximo;
    });

  const marcarVarios = (pedidos: PedidoFila[], marcar: boolean): void =>
    definirMarcados((antes) => {
      const proximo = new Set(antes);
      for (const p of pedidos) {
        if (marcar) proximo.add(p.televenda);
        else proximo.delete(p.televenda);
      }
      return proximo;
    });

  const todosVisiveisMarcados =
    visiveis.length > 0 && visiveis.every((p) => marcados.has(p.televenda));

  /**
   * Fatura os marcados, um por vez, mostrando cada resultado quando chega.
   *
   * Sequencial porque a procedure fiscal emite documento numerado e é pesada —
   * disparar dez em paralelo é pedir deadlock. O que muda em relação ao app
   * antigo é que nada bloqueia: a linha muda de cor sozinha, o contador anda,
   * e "Parar" interrompe antes do próximo pedido (o que já foi para o banco,
   * foi — nota emitida não se desfaz por aqui).
   */
  async function faturarSelecionados(): Promise<void> {
    const alvos = fila.filter((p) => marcados.has(p.televenda));
    if (alvos.length === 0) return;

    cancelar.current = false;
    definirLote({ rodando: true, feitos: 0, total: alvos.length, porPedido: {} });

    for (const [indice, pedido] of alvos.entries()) {
      if (cancelar.current) break;
      const volume = volumeDe(pedido);
      let resposta: { ok: boolean; mensagem: string };
      try {
        const r = await faturar(pedido.televenda, volume, cracha, informado);
        resposta = { ok: true, mensagem: r.mensagem };
        aoAnotar({ televenda: pedido.televenda, tom: 'ok', mensagem: r.mensagem, volume });
      } catch (err) {
        const erro = err instanceof ErroHttp ? err : null;
        resposta = { ok: false, mensagem: erro?.message ?? 'Falha ao falar com o servidor.' };
        aoAnotar({
          televenda: pedido.televenda,
          tom: erro?.status === 409 ? 'recusado' : 'falhou',
          mensagem: resposta.mensagem,
          volume: null,
        });
      }

      definirLote((antes) => ({
        ...antes,
        feitos: indice + 1,
        porPedido: { ...antes.porPedido, [pedido.televenda]: resposta },
      }));
      // Faturado sai da seleção: o que sobra marcado é o que ainda falta.
      if (resposta.ok) {
        definirMarcados((antes) => new Set([...antes].filter((t) => t !== pedido.televenda)));
      }
    }

    definirLote((antes) => ({ ...antes, rodando: false }));
    void consulta.refetch();
  }

  if (consulta.isLoading) return <Esqueleto className="h-64" />;
  if (consulta.isError) {
    return <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Segmentado
          tamanho="pequeno"
          valor={escopo}
          aoMudar={aoMudarEscopo}
          rotuloAcessivel="De quem é a fila"
          opcoes={[
            { valor: 'meu', rotulo: 'Meu crachá' },
            { valor: 'empresa', rotulo: 'Empresa inteira' },
          ]}
        />

        {fila.length > 0 ? (
          <>
            <span className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-textoFraco" />
              <input
                value={busca}
                onChange={(e) => definirBusca(e.target.value)}
                placeholder="filtrar pedido, canal, modal ou cidade"
                aria-label="Filtrar a grade"
                className="min-h-[34px] w-[260px] rounded-lg border border-borda bg-superficie pl-8 pr-2 text-[12.5px]"
              />
            </span>

            <button
              type="button"
              onClick={() => marcarVarios(visiveis, !todosVisiveisMarcados)}
              className="flex items-center gap-1.5 rounded-lg border border-borda px-2.5 py-1.5 text-[12.5px] font-semibold hover:bg-superficie2"
            >
              {todosVisiveisMarcados ? (
                <SquareCheck className="h-4 w-4 text-primaria" />
              ) : (
                <Square className="h-4 w-4 text-textoSuave" />
              )}
              {todosVisiveisMarcados ? 'Desmarcar' : 'Marcar'} {busca ? 'os filtrados' : 'todos'}
            </button>
          </>
        ) : null}

        {escopo === 'empresa' ? (
          <p className="text-[11.5px] text-textoFraco">
            Inclusive a fila de outros crachás — para achar o que ficou para trás.
          </p>
        ) : null}
      </div>

      {fila.length === 0 ? (
        <Cartao>
          <CorpoCartao>
            <EstadoVazio
              icone={<Layers className="h-7 w-7" />}
              titulo="Nada esperando nota agora"
              detalhe={
                escopo === 'meu'
                  ? 'Nenhum pedido conferido na fila deste crachá. Pedido conferido cai aqui sozinho, assim que a conferência fecha — veja também a fila da empresa inteira.'
                  : 'Nenhum pedido conferido esperando nota nesta empresa.'
              }
            />
          </CorpoCartao>
        </Cartao>
      ) : visiveis.length === 0 ? (
        <Cartao>
          <CorpoCartao>
            <EstadoVazio titulo={`Nenhum pedido com “${busca}”`} />
          </CorpoCartao>
        </Cartao>
      ) : (
        <div className="space-y-3">
          {ondas.map(([modal, pedidos]) => {
            const todosMarcados = pedidos.every((p) => marcados.has(p.televenda));
            return (
              <Cartao key={modal}>
                <div className="flex flex-wrap items-center gap-2 border-b border-borda px-4 py-2.5">
                  <button
                    type="button"
                    onClick={() => marcarVarios(pedidos, !todosMarcados)}
                    className="flex items-center gap-2 text-left"
                    title={`${todosMarcados ? 'Desmarcar' : 'Marcar'} a onda ${modal}`}
                  >
                    {todosMarcados ? (
                      <SquareCheck className="h-[18px] w-[18px] text-primaria" />
                    ) : (
                      <Square className="h-[18px] w-[18px] text-textoSuave" />
                    )}
                    <span className="text-[14px] font-bold">{modal}</span>
                  </button>
                  <Pilula tom="neutro">
                    {pedidos.length} {pedidos.length === 1 ? 'pedido' : 'pedidos'}
                  </Pilula>
                  <span className="ml-auto text-[11.5px] text-textoFraco">
                    {numero(pedidos.reduce((s, p) => s + p.itens, 0))} unidades
                  </span>
                </div>

                <CabecalhoColunas />

                <ul className="divide-y divide-borda">
                  {pedidos.map((p) => (
                    <LinhaFila
                      key={p.televenda}
                      pedido={p}
                      marcado={marcados.has(p.televenda)}
                      volume={volumeDe(p)}
                      resultado={lote.porPedido[p.televenda]}
                      aoAlternar={() => alternar(p.televenda)}
                      aoMudarVolume={(v) =>
                        definirVolumes((antes) => ({ ...antes, [p.televenda]: v }))
                      }
                    />
                  ))}
                </ul>
              </Cartao>
            );
          })}
        </div>
      )}

      {marcados.size > 0 || lote.rodando ? (
        <div className="sticky bottom-3 z-10 flex flex-wrap items-center gap-3 rounded-xl border border-borda bg-superficie px-4 py-3 shadow-lg">
          <span className="text-[13px]">
            <strong className="tabular">{marcados.size}</strong> selecionado
            {marcados.size === 1 ? '' : 's'}
          </span>
          {lote.rodando ? (
            <>
              <span className="tabular text-[12.5px] text-textoFraco">
                faturando {Math.min(lote.feitos + 1, lote.total)} de {lote.total}…
              </span>
              <span className="h-1.5 w-32 overflow-hidden rounded-full bg-superficie3">
                <span
                  className="gradiente-marca block h-full rounded-full transition-[width]"
                  style={{ width: `${(lote.feitos / Math.max(1, lote.total)) * 100}%` }}
                />
              </span>
              <Botao
                variante="fantasma"
                tamanho="pequeno"
                className="ml-auto"
                onClick={() => {
                  cancelar.current = true;
                }}
              >
                Parar depois deste
              </Botao>
            </>
          ) : (
            <Botao variante="primaria" className="ml-auto" onClick={() => void faturarSelecionados()}>
              <FileText className="h-4 w-4" />
              Faturar {marcados.size} {marcados.size === 1 ? 'pedido' : 'pedidos'}
            </Botao>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * As colunas da fila, num lugar só.
 *
 * Cabeçalho e linha usam a MESMA grade — é o que mantém os números debaixo do
 * rótulo certo quando a descrição de um produto é longa. Abaixo de `md` a
 * grade colapsa em três colunas (marcar · pedido · volume) e destino e SKUs
 * descem para linhas próprias, porque em tablet estreito espremer cinco
 * colunas só produz texto cortado.
 */
const GRADE_FILA =
  'grid items-center gap-x-3 gap-y-1.5 ' +
  'grid-cols-[22px_minmax(0,1fr)_auto] ' +
  'md:grid-cols-[22px_104px_minmax(0,1fr)_minmax(0,1.7fr)_112px]';

/** A régua de rótulos. Só em `md`, onde a grade de fato tem cinco colunas. */
function CabecalhoColunas(): JSX.Element {
  return (
    <div className={cn(GRADE_FILA, 'hidden border-b border-borda px-4 py-1.5 md:grid')}>
      <span />
      <span className="rotulo">Pedido</span>
      <span className="rotulo">Canal e destino</span>
      <span className="rotulo">Itens do pedido</span>
      <span className="rotulo text-center">Volumes</span>
    </div>
  );
}

/**
 * Os SKUs do pedido, na própria linha.
 *
 * Esta é a informação que decide o faturamento: quem está na expedição precisa
 * bater o que está na caixa com o que o pedido tem, e antes isso vivia só num
 * `title` — informação que só existe para quem já sabe que precisa passar o
 * mouse. Agora cada SKU é uma pastilha com código e quantidade.
 *
 * Um pedido continua sendo UMA linha: vários SKUs fazem as pastilhas quebrarem
 * dentro da célula, nunca repetem o pedido. A quantidade só ganha cor quando é
 * maior que 1, que é o caso em que errar o volume custa caro.
 */
function Skus({ produtos }: { produtos: PedidoFila['produtos'] }): JSX.Element {
  if (produtos.length === 0) {
    return <span className="text-[12px] text-textoSuave">sem itens</span>;
  }

  // Poucos SKUs cabem inteiros; muitos viram contagem, senão a linha vira um
  // parágrafo e a grade inteira perde o alinhamento.
  const LIMITE = 4;
  const visiveis = produtos.slice(0, LIMITE);
  const resto = produtos.length - visiveis.length;
  const todos = produtos
    .map((p) => `${p.produto} x${p.quantidade}  ${p.descricao}`)
    .join('\n');

  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1" title={todos}>
      {visiveis.map((p) => (
        <span
          key={p.produto}
          className="inline-flex min-w-0 max-w-[210px] items-baseline gap-1 rounded-md border border-borda bg-superficie2 px-1.5 py-[3px] text-[11px] leading-none"
        >
          <span className="tabular shrink-0 font-mono font-semibold">{p.produto}</span>
          <span
            className={cn(
              'tabular shrink-0 font-bold',
              p.quantidade > 1 ? 'text-primaria' : 'text-textoSuave',
            )}
          >
            ×{p.quantidade}
          </span>
          <span className="truncate text-textoFraco">{p.descricao}</span>
        </span>
      ))}
      {resto > 0 ? (
        <span className="tabular text-[11px] font-semibold text-textoFraco">
          +{resto} {resto === 1 ? 'item' : 'itens'}
        </span>
      ) : null}
    </span>
  );
}

function LinhaFila({
  pedido,
  marcado,
  volume,
  resultado,
  aoAlternar,
  aoMudarVolume,
}: {
  pedido: PedidoFila;
  marcado: boolean;
  volume: number;
  resultado: { ok: boolean; mensagem: string } | undefined;
  aoAlternar: () => void;
  aoMudarVolume: (v: number) => void;
}): JSX.Element {
  const destino = [pedido.cidade, pedido.uf].filter(Boolean).join(' · ');
  const alterado = volume !== 1;

  return (
    <li
      className={cn(
        GRADE_FILA,
        'px-4 py-2.5 transition-colors',
        resultado?.ok === true && 'bg-status-livreFundo',
        resultado?.ok === false && 'bg-status-divergenciaFundo',
        marcado && !resultado && 'bg-primaria-suave',
      )}
    >
      <button
        type="button"
        onClick={aoAlternar}
        className="md:row-start-1 md:col-start-1"
        aria-label={`Selecionar pedido ${pedido.televenda}`}
      >
        {marcado ? (
          <SquareCheck className="h-[18px] w-[18px] text-primaria" />
        ) : (
          <Square className="h-[18px] w-[18px] text-textoSuave" />
        )}
      </button>

      <span className="min-w-0 md:col-start-2 md:row-start-1">
        <span className="tabular block font-mono text-[13.5px] font-bold leading-tight">
          {pedido.televenda}
        </span>
        <span className="tabular block text-[11px] text-textoFraco">
          {pedido.data ? new Date(pedido.data).toLocaleDateString('pt-BR') : '—'}
        </span>
      </span>

      {/*
        O volume fica na ÚLTIMA coluna em md e logo à direita do pedido no
        estreito: é o único campo editável da linha, e tem que estar sempre à
        mão do polegar, não no fim de uma linha que quebrou.
      */}
      <label
        className="flex items-center justify-self-end md:col-start-5 md:row-start-1 md:justify-self-center"
        title="Quantas caixas vão nesta expedição"
      >
        <span className="rotulo mr-1.5 md:hidden">vol</span>
        <input
          value={volume}
          onChange={(e) => aoMudarVolume(Math.max(1, Math.min(999, Number(e.target.value) || 1)))}
          inputMode="numeric"
          aria-label={`Volumes do pedido ${pedido.televenda}`}
          className={cn(
            'tabular h-9 w-14 rounded-lg border bg-superficie text-center font-mono text-[14px] font-bold',
            alterado ? 'border-primaria text-primaria' : 'border-borda',
          )}
        />
      </label>

      <span className="col-span-3 min-w-0 md:col-span-1 md:col-start-3 md:row-start-1">
        <span className="block truncate text-[12.5px] font-semibold" title={pedido.canal ?? ''}>
          {pedido.canal ?? '—'}
        </span>
        <span className="block truncate text-[11.5px] text-textoFraco">{destino || '—'}</span>
      </span>

      <span className="col-span-3 min-w-0 md:col-span-1 md:col-start-4 md:row-start-1">
        <Skus produtos={pedido.produtos} />
        <span className="tabular mt-0.5 block text-[10.5px] text-textoSuave">
          {pedido.produtos.length} {pedido.produtos.length === 1 ? 'SKU' : 'SKUs'} ·{' '}
          <strong className="font-semibold text-textoFraco">{numero(pedido.itens)} un</strong>{' '}
          compradas
        </span>
      </span>

      {resultado ? (
        <span
          className={cn(
            'col-span-full flex items-start gap-1.5 text-[12px]',
            resultado.ok ? 'text-status-livre' : 'text-status-divergencia',
          )}
        >
          {resultado.ok ? (
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          ) : (
            <X className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          )}
          {resultado.mensagem}
        </span>
      ) : null}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Problemas — a lista que no app antigo é um beco sem saída
// ---------------------------------------------------------------------------

function PainelProblemas({
  consulta,
  cracha,
  informado,
  aoAnotar,
}: {
  consulta: ReturnType<typeof useQuery<RespostaFila>>;
  cracha: string;
  informado: boolean;
  aoAnotar: (r: Omit<Registro, 'chave' | 'quando'>) => void;
}): JSX.Element {
  const falhas = consulta.data?.falhas ?? [];
  const [refazendo, definirRefazendo] = React.useState<number | null>(null);

  async function tentarDeNovo(televenda: number, volume: number): Promise<void> {
    definirRefazendo(televenda);
    try {
      const r = await faturar(televenda, volume, cracha, informado);
      aoAnotar({ televenda, tom: 'ok', mensagem: r.mensagem, volume: r.volume });
    } catch (err) {
      const erro = err instanceof ErroHttp ? err : null;
      aoAnotar({
        televenda,
        tom: erro?.status === 409 ? 'recusado' : 'falhou',
        mensagem: erro?.message ?? 'Falha ao falar com o servidor.',
        volume: null,
      });
    } finally {
      definirRefazendo(null);
      void consulta.refetch();
    }
  }

  if (consulta.isLoading) return <Esqueleto className="h-48" />;

  if (falhas.length === 0) {
    return (
      <Cartao>
        <CorpoCartao>
          <EstadoVazio
            icone={<Check className="h-7 w-7" />}
            titulo="Nenhuma falha nos últimos 7 dias"
            detalhe="Nada travou no caminho entre conferir e faturar."
          />
        </CorpoCartao>
      </Cartao>
    );
  }

  return (
    <Cartao>
      <CorpoCartao className="space-y-2">
        <p className="text-[12px] leading-relaxed text-textoFraco">
          Tentativas que o ERP recusou — nossas e do MegaFaturamento, porque é a mesma tabela (
          <code>FATURAMENTOS_ERROS</code>). Quem está <strong>resolvido</strong> saiu depois, em
          outra tentativa; quem está <strong>parado</strong> ainda não tem nota.
        </p>
        <ul className="divide-y divide-borda">
          {falhas.map((f) => (
            <li key={f.id} className="flex flex-wrap items-start gap-x-3 gap-y-1 py-2">
              <span className="tabular w-[80px] font-mono text-[13.5px] font-bold">
                {f.televenda}
              </span>
              <span className="tabular w-[110px] text-[11.5px] text-textoFraco">
                {new Date(f.quando).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              {f.resolvido ? (
                <Pilula tom="ok">resolvido</Pilula>
              ) : (
                <Pilula tom="erro">parado</Pilula>
              )}
              {!f.resolvido ? (
                <Botao
                  variante="fantasma"
                  tamanho="pequeno"
                  carregando={refazendo === f.televenda}
                  onClick={() => void tentarDeNovo(f.televenda, 1)}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Tentar de novo
                </Botao>
              ) : null}
              <span className="w-full text-[12px] leading-snug text-textoFraco">{f.erro}</span>
            </li>
          ))}
        </ul>
      </CorpoCartao>
    </Cartao>
  );
}

/** O que aconteceu nesta sessão de trabalho, do mais recente para trás. */
function Historico({
  registros,
  aoLimpar,
}: {
  registros: Registro[];
  aoLimpar: () => void;
}): JSX.Element {
  return (
    <Cartao>
      <CorpoCartao className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="rotulo">Nesta sessão</h2>
          <Botao variante="fantasma" tamanho="pequeno" onClick={aoLimpar}>
            Limpar
          </Botao>
        </div>

        <ul className="space-y-1.5">
          {registros.map((r) => (
            <li
              key={r.chave}
              className={cn(
                'flex items-start gap-2 rounded-lg px-2.5 py-1.5',
                r.tom === 'ok'
                  ? 'bg-status-livreFundo'
                  : r.tom === 'recusado'
                    ? 'bg-status-atencaoFundo'
                    : 'bg-status-divergenciaFundo',
              )}
            >
              <span className="mt-0.5 shrink-0">
                {r.tom === 'ok' ? (
                  <Check className="h-4 w-4 text-status-livre" />
                ) : r.tom === 'recusado' ? (
                  <AlertTriangle className="h-4 w-4 text-status-atencao" />
                ) : (
                  <X className="h-4 w-4 text-status-divergencia" />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline gap-x-2">
                  <span className="tabular font-mono text-[13.5px] font-bold">{r.televenda}</span>
                  {r.volume !== null ? (
                    <span className="text-[11px] text-textoFraco">
                      {r.volume} {r.volume === 1 ? 'volume' : 'volumes'}
                    </span>
                  ) : null}
                  <span className="tabular ml-auto text-[11px] text-textoFraco">
                    {new Date(r.quando).toLocaleTimeString('pt-BR')}
                  </span>
                </span>
                <span className="block text-[12.5px] leading-snug">{r.mensagem}</span>
              </span>
            </li>
          ))}
        </ul>
      </CorpoCartao>
    </Cartao>
  );
}

// ---------------------------------------------------------------------------
// Rejeitadas na SEFAZ — a nota que saiu do WMS mas não virou documento
// ---------------------------------------------------------------------------

/**
 * As NF-e pendentes na SEFAZ: recusadas, com falha no envio ou aguardando o
 * robô (NFE_CABECALHO.STATUS = 2) — de venda e de devolução.
 *
 * É outra natureza de problema, e por isso é outra aba: em "Problemas" está o
 * pedido que NÃO virou nota; aqui está a nota que foi emitida, saiu do WMS e
 * foi recusada lá fora. Quem resolve costuma ser outra pessoa — o motivo quase
 * sempre é cadastro do cliente ("IE do destinatário não informada"), não
 * operação de CD.
 *
 * Só o que está PENDENTE. A nota que já foi corrigida e reenviada não é
 * trabalho de ninguém, e o corte acontece no SQL — a tela não recebe o que não
 * vai mostrar. A lista é da empresa inteira, sem filtro de crachá: quem
 * conserta cadastro de cliente não é quem faturou.
 */
/** "atualizado às 14:32:05" se for hoje; "28/10 14:32:05" se não. */
function horaDaVerificacao(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  const hora = `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  return d.toDateString() === new Date().toDateString()
    ? `atualizado às ${hora}`
    : `${p(d.getDate())}/${p(d.getMonth() + 1)} ${hora}`;
}

/**
 * Quando foi a última busca, e o ↻ para buscar de novo.
 *
 * A hora é a do navegador: `dataUpdatedAt` é o `Date.now()` do cliente quando
 * a busca (manual ou automática) volta com sucesso, no fuso de quem olha. A
 * rota não tem cache no servidor, então cada busca vai de fato ao NFE_LOG.
 */
function BotaoVerificarDeNovo({
  consulta,
}: {
  consulta: ReturnType<typeof useQuery<RespostaRejeicoes>>;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => void consulta.refetch()}
      disabled={consulta.isFetching}
      title="Verificar de novo na base"
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11.5px] text-textoFraco hover:bg-superficie2 hover:text-texto disabled:opacity-60"
    >
      <RefreshCw className={cn('h-3.5 w-3.5', consulta.isFetching && 'animate-spin')} />
      {consulta.isFetching
        ? 'verificando…'
        : consulta.dataUpdatedAt
          ? horaDaVerificacao(consulta.dataUpdatedAt)
          : 'verificar'}
    </button>
  );
}

function PainelRejeicoes({
  consulta,
}: {
  consulta: ReturnType<typeof useQuery<RespostaRejeicoes>>;
}): JSX.Element {
  if (consulta.isLoading) return <Esqueleto className="h-64" />;
  if (consulta.isError) {
    return (
      <Cartao>
        <CorpoCartao>
          <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
        </CorpoCartao>
      </Cartao>
    );
  }

  const todas = consulta.data?.rejeicoes ?? [];
  const deTodas = consulta.data?.empresa === 'todas';
  if (todas.length === 0) {
    return (
      <Cartao>
        <CorpoCartao>
          <div className="flex justify-end">
            <BotaoVerificarDeNovo consulta={consulta} />
          </div>
          <EstadoVazio
            icone={<BadgeCheck className="h-7 w-7" />}
            titulo="Nenhuma nota pendente na SEFAZ"
            detalhe={`Nos últimos 15 dias, nenhuma nota de venda ou de devolução${
              deTodas ? ', em nenhuma empresa,' : ''
            } está recusada ou parada aguardando envio.`}
          />
        </CorpoCartao>
      </Cartao>
    );
  }

  return (
    <Cartao>
      <div className="flex flex-wrap items-center gap-2 border-b border-borda px-4 py-2.5">
        <FileWarning className="h-4 w-4 text-status-divergencia" />
        <span className="text-[13px] font-bold">Pendentes na SEFAZ</span>
        <Pilula tom={todas.length ? 'alerta' : 'neutro'}>
          {numero(todas.length)} {todas.length === 1 ? 'pendente' : 'pendentes'}
        </Pilula>
        <span className="ml-auto text-[11.5px] text-textoFraco">
          {deTodas ? 'todas as empresas · ' : ''}últimos 15 dias
        </span>
        <BotaoVerificarDeNovo consulta={consulta} />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px] border-collapse text-[12.5px]">
          <thead>
            <tr className="border-b border-borda text-left">
              {deTodas ? <th className="rotulo py-1.5 pl-4 pr-2">Empresa</th> : null}
              <th className={cn('rotulo py-1.5 pr-2', !deTodas && 'pl-4')}>Origem</th>
              <th className="rotulo py-1.5 pr-2">Televenda</th>
              <th className="rotulo py-1.5 pr-2">Nota</th>
              <th className="rotulo py-1.5 pr-2">Emitida em</th>
              <th className="rotulo py-1.5 pr-4">Situação</th>
            </tr>
          </thead>
          <tbody>
            {todas.map((r) => (
              <tr
                key={`${r.origem}-${r.empresa}-${r.nfNumero ?? r.televenda}-${r.quando}`}
                className="border-b border-borda/60 align-top"
              >
                {deTodas ? (
                  <td className="py-2 pl-4 pr-2">
                    <span className="tabular font-mono font-semibold">{r.empresa ?? '—'}</span>
                    {r.empresaNome ? <span className="text-textoFraco"> · {r.empresaNome}</span> : null}
                  </td>
                ) : null}
                <td className={cn('py-2 pr-2', !deTodas && 'pl-4')}>
                  {r.origem === 'devolucao' ? (
                    <span title={r.devolucao ? `DEV_PRODUTOS_CAIXAS ${r.devolucao}` : undefined}>
                      Devolução
                      {r.devolucao ? (
                        <span className="tabular font-mono text-textoFraco"> {r.devolucao}</span>
                      ) : null}
                    </span>
                  ) : (
                    'Venda'
                  )}
                </td>
                <td className="tabular py-2 pr-2 font-mono font-semibold">{r.televenda ?? '—'}</td>
                <td className="tabular py-2 pr-2 font-mono">{r.nfNumero ?? '—'}</td>
                <td className="tabular py-2 pr-2 text-textoFraco">
                  {r.dataNf
                    ? new Date(r.dataNf).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '—'}
                </td>
                <td className="py-2 pr-4">
                  <span className="flex flex-wrap items-center gap-1.5">
                    {/* A etapa distingue recusa, falha no envio e nota ainda na fila do robô. */}
                    {r.etapa === 'AGUARDANDO' ? (
                      <span className="inline-block rounded bg-superficie2 px-1.5 py-[3px] text-[11.5px] font-bold leading-none text-textoFraco">
                        aguardando processar
                      </span>
                    ) : (
                      <span className="inline-block rounded bg-status-divergenciaFundo px-1.5 py-[3px] text-[11.5px] font-bold leading-none text-status-divergencia">
                        {r.etapa === 'ERRO' ? 'falha no envio' : 'recusada pela SEFAZ'}
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block text-[12px]">{r.motivo}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Cartao>
  );
}
